import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency, parseCurrency } from 'utils/i18n';
import groupBy from '../../utils/groupBy';
import ensureKeyed from '../../utils/ensureKeyed';
import {contracts, fields} from '../../ducks/index';
import registeredComponent from "utils/registeredComponent";
import duckify, {singleDuck} from "utils/duckify";
import Placeholder from '../Placeholder';
import classNames from 'classnames';
import {sortBy} from '../../utils/arrays';
import {titleize} from "utils";
import SelectPicker from 'components/SelectPicker/SelectPicker';
import SplitContractSelector from '../contracts/SplitContractSelector';
import FeeListItemAmount from './FeeListItemAmount';
import { CONTRACT_CHARGE_TYPES, DISCOUNT_TYPES } from './ChargeTypes';

const generateContractChargeMap = (contract) => {
  const charges = (contract.contract_charges || []).sort(sortBy('name'));

  return groupBy(charges, 'charge_type');
};

const getSplitAmount =  ({contractFee, currentSplit}) => {
  if (!currentSplit) {
    return 0;
  }

  if (contractFee) {
    return parseCurrency(currentSplit.charge_splits[contractFee.id], contractFee) || 0;
  }

  return 0;
};

const StaticContractFeeItem = ({
  contract,
  contractFee,
  currentSplit
}) => {
  const splitAmount = getSplitAmount({contractFee, currentSplit}) || 0;

  // return nothing if the split amount is 0 and it's a tuition discount
  if (contract.has_multiple_splits && splitAmount === 0 && contractFee && DISCOUNT_TYPES.find(x => x === contractFee.charge_type)) {
    return <React.Fragment></React.Fragment>;
  }

  return <div className={`fee-list-item fee-list-item-selected`}>
    <div className={'sa-form-tuition-item-name'}>
      {contractFee.name}
    </div>

    <FeeListItemAmount contract_id={contract.id} contractFee={contractFee}/>
    {
      !!contractFee.description &&
        <div className={'sa-form-tuition-item-description'}
             dangerouslySetInnerHTML={{__html: contractFee.description}}
        />
    }
  </div>;
};

const SelectableContractFeeItem = ({contractFee, contract, readonly, onChange, duckMeta, currentSplit}) => {
  const isOptionalField = !!contractFee.field_id;

  const checked = isOptionalField
    ? !!contract.optional_fee_field_ids.find(f => f === contractFee.field_id)
    : !!contract.tuition_fee_ids.find(f => f === contractFee.contract_fee_id);

  // We couldn't just store discounts etc as negative numbers.  Nope, convert them at the UI.
  const amount = DISCOUNT_TYPES.find(x => x === contractFee.charge_type)
    ? -1 * contractFee.amount
    : contractFee.amount;

  const fieldId = `${contract.form_name}[${isOptionalField ? 'optional_fee_field_ids' : 'tuition_fee_ids'}][]`;

  return <div className={classNames('fee-list-item', {'fee-list-item-selected': checked})}>
      <div className={'sa-form-tuition-item-name'}>
        <div className={'form-check'}>
          <input
            type={'checkbox'}
            id={fieldId}
            name={fieldId}
            value={isOptionalField ? contractFee.field_id : contractFee.contract_fee_id}
            defaultChecked={checked}
            disabled={!!readonly || duckMeta.contract.writing}
            className={'form-check-input fee-list-option'}
            onChange={onChange}
          />
          <label className={'form-check-label'} htmlFor={fieldId}>
            {contractFee.name}
            &nbsp;
            {amount !== 0 && (
              <span className={'fee-list-item-amount-inline'}>
                ({formatCurrency(amount)})
              </span>
            )}
          </label>
        </div>
      </div>

      <FeeListItemAmount contract_id={contract.id} contractFee={contractFee}/>

      {
        !!contractFee.description &&
        <div className={'sa-form-tuition-item-description'}
             dangerouslySetInnerHTML={{__html: contractFee.description}}
        />
      }
  </div>;
};

const TuitionField = ({
  contract,
  readonly,
  updateContract,
  allDucksReady,
  custom_total_text,
  duckMeta,
  selectedBillingPayerId,
  selectBillingPayer
}) => {
  const onChange = (event) => {
    const { checked, value, name } = event.target;

    const isOptionalFeeField = (name === 'contract[optional_fee_field_ids][]');
    const key = isOptionalFeeField ? 'optional_fee_field_ids' : 'tuition_fee_ids';

    if (checked) {
      updateContract({
        ...contract,
        [key]: Array.from(new Set([...contract[key], value]))
      });
    } else {
      updateContract({
        ...contract,
        [key]: contract[key].filter(x => x !== value) || []
      });
    }
  };

  return <Placeholder.formField ready={allDucksReady} prompt={'Tuition & Fees'} content={() => {
    const amountPlaceholder = (amount) => <Placeholder.text ready={allDucksReady && !duckMeta.contract.writing} lines={1} width={160}>
      <span className={'amount'}>{formatCurrency(amount)}</span>
    </Placeholder.text>;

    const contractChargeMap = generateContractChargeMap(contract);

    const chargesByType = (type) => contractChargeMap[type] || [];

    const currentSplit = contracts.hooks.useBillingPayerSplit(contract, selectedBillingPayerId);

    return <React.Fragment>
      <div className={'sa-form-field-group sa-form-tuition-group-header-row'}>
        <div className={'sa-form-field-group-prompt'}>
          Base Tuition
        </div>
        <div className={'sa-form-field-group-body'}>
          <div className={'fee-list-item fee-list-item-selected'}>
            <div className={'sa-form-tuition-item-name'}>
            </div>

            {contract.has_multiple_splits && (
              <div className={'fee-list-item-amount fee-list-item-amount-payer-split'}>
                <SplitContractSelector contract_id={contract.id}/>
              </div>
            )}

            <div className={'fee-list-item-amount'}>
              Total
            </div>
          </div>
        </div>
      </div>

      <div className={'sa-form-field-group'}>
        <div className={'sa-form-field-group-prompt'}></div>
        <div className={'sa-form-field-group-body'}>
          {
            [
              CONTRACT_CHARGE_TYPES.TUITION,
              CONTRACT_CHARGE_TYPES.TUITION_DISCOUNT,
              CONTRACT_CHARGE_TYPES.TUITION_AID
            ].map(chargeType =>
              chargesByType(chargeType)
                .map(contractFee => StaticContractFeeItem({
                  contract,
                  contractFee,
                  currentSplit
                }))
                .map(ensureKeyed(`${chargeType}_fees`))
            )
          }
        </div>
      </div>

      {chargesByType(CONTRACT_CHARGE_TYPES.REQUIRED_FEE).some(x=>x) &&
      <React.Fragment>
        <div className={'sa-form-field-group sa-form-tuition-group-header-row'}>
          <div className={'sa-form-field-group-prompt'}>
            <div className={'sa-form-tuition-group-header'}>
              Additional Fees
            </div>
          </div>
        </div>
        <div className={'sa-form-field-group sa-form-tuition-required-fees'}>
          <div className={'sa-form-field-group-prompt'}></div>
          <div className={'sa-form-field-group-body'}>
            {
              chargesByType(CONTRACT_CHARGE_TYPES.REQUIRED_FEE)
              .map(contractFee => StaticContractFeeItem({
                contract,
                contractFee,
                currentSplit
              }))
                .map(ensureKeyed('required_fees'))
            }
          </div>
        </div>
      </React.Fragment>
      }

      {chargesByType(CONTRACT_CHARGE_TYPES.OPTIONAL_FEE).some(x=>x) &&
      <React.Fragment>
        <div className={'sa-form-field-group sa-form-tuition-group-header-row'}>
          <div className={'sa-form-field-group-prompt'}>
            <div className={'sa-form-tuition-group-header'}>
              Optional Fees
            </div>
            <div className={'sa-form-tuition-group-header-details'}>
              Select all that apply
            </div>
          </div>
        </div>

        <input type={'hidden'} value= {''} name={`${contract.form_name}[tuition_fee_ids][]`} disabled={!!readonly}/>

        <div className={'sa-form-field-group'}>
          <div className={'sa-form-field-group-prompt'}></div>
          <div className={'sa-form-field-group-body sa-form-tuition-optional-fees'}>
            {
              chargesByType(CONTRACT_CHARGE_TYPES.OPTIONAL_FEE)
                .map(contractFee => SelectableContractFeeItem({
                  contractFee,
                  contract,
                  readonly,
                  duckMeta,
                  currentSplit,
                  onChange
                }))
                .map(ensureKeyed('optional_fees'))
            }
          </div>
        </div>
      </React.Fragment>
      }

      <div className={'sa-form-field-group fee-list-group-total-row'}>
        <div className={'sa-form-field-group-prompt'}>
          {custom_total_text || 'Tuition Total'}
        </div>
        <div className={'sa-form-field-group-body'}>
          <div className={'fee-list-item'}>
            <div className={'sa-form-tuition-item-name'}>
            </div>

            <FeeListItemAmount contract_id={contract.id} amountProperty={'tuition_and_fees_total'}/>
          </div>
        </div>
      </div>
    </React.Fragment>}
  }/>;
};

TuitionField.propTypes = {
  contract_id: PropTypes.string.isRequired,
  readonly: PropTypes.bool,
  custom_total_text: PropTypes.string,
  updateContract: PropTypes.func, // ([id1, id2]) => Promise.resolve...
};

const mapDucksToProps = (props) => ({
  contract: singleDuck(contracts, props.contract_id),
});

const mapStateToProps = (state, props) => ({
  selectedBillingPayerId: state.contracts.currentContract.selectedBillingPayerId
});

const mapDispatchToProps = (dispatch, props) => ({
  updateContract: (contract) => dispatch(contracts.actions.update(contract)),
  selectBillingPayer: (billingPayerId) => dispatch(contracts.actions.selectBillingPayer(billingPayerId))
});

export default registeredComponent('TuitionField')(duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(TuitionField));
